import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AutoComplete, AutoCompleteOption } from '@wayste/sour-ui';
import { formatServiceAddress, intlTimezoneToRelativeTimezoneMap } from '@wayste/client-utils';
import { Loader } from '@googlemaps/js-api-loader';
import debounce from 'lodash.debounce';
import { useFormContext } from 'react-hook-form';
import { fuzzySearch } from '../utils/fuzzy-search';
const fetchTimezoneForLatLng = async (lat, lng, apiKey) => {
    const timestamp = Math.floor(Date.now() / 1000);
    const resp = await fetch(`https://maps.googleapis.com/maps/api/timezone/json?location=${lat},${lng}&timestamp=${timestamp}&key=${apiKey}`);
    const data = await resp.json();
    if (data.status === 'OK') {
        return {
            timeZoneId: intlTimezoneToRelativeTimezoneMap[data.timeZoneId] ?? data.timeZoneId, // e.g. "America/New_York". We transform it so the number of potential entries is limited for better human readability.
            timeZoneName: data.timeZoneName, // e.g. "Eastern Daylight Time"
            rawOffset: data.rawOffset, // seconds from UTC (standard)
            dstOffset: data.dstOffset, // seconds of DST shift
        };
    }
    // Soft-fail: return null so selection still works if TZ lookup fails.
    console.warn('Time Zone API error:', data.status, data.errorMessage);
    return null;
};
/**
 * GooglePlacesInput component integrates Google Places Autocomplete allowing users to search for places and select a location.
 * The properties for configuring this component can be found at {@link GooglePlacesInputProps}
 */
export function GooglePlacesInput({ fieldName, apiKey, onPlaceSelected, onError, inputProps, required = false, partial = false, disabled = false, showFullAddressInInput = false, searchOptions, showCountry, includeTimezone = false, savedAddresses, }) {
    const [loadingResults, setLoadingResults] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);
    const loader = new Loader({
        apiKey: apiKey,
        version: 'weekly',
        libraries: ['places', 'core', 'geocoding'],
        id: 'google-maps-script',
    });
    const places = useRef(undefined);
    useEffect(() => {
        setIsLoaded(false);
        (async () => {
            places.current = await loader.importLibrary('places');
            setIsLoaded(true);
        })();
    }, []);
    const [hasValue, setHasValue] = useState(false);
    const [displayValue, setDisplayValue] = useState(inputProps?.queryValue || ''); //only for rendering SELECTED values, not actual live query value
    const [query, setQuery] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const methods = useFormContext();
    const emptyAddress = {
        address: {
            addressLine1: '',
            city: '',
            state: '',
            zip: '',
            country: '',
        },
        coordinates: {
            lat: 0,
            lng: 0,
        },
        county: '',
    };
    const countyCleaner = (cleanAddress, county) => {
        if (cleanAddress.city === 'Baltimore') {
            return 'Baltimore City';
        }
        else if (cleanAddress.state === 'DC') {
            return 'District of Columbia';
        }
        else if (!county) {
            return '';
        }
        else {
            return county?.longText?.replace(' County', '') || '';
        }
    };
    // Filter saved addresses based on query
    const filteredSavedAddresses = useMemo(() => {
        if (!savedAddresses || query.length === 0)
            return savedAddresses || [];
        return fuzzySearch(query, savedAddresses, (address) => formatServiceAddress(address.address));
    }, [savedAddresses, query]);
    const handleSelect = async (selectedValue) => {
        if (selectedValue === null) {
            setDisplayValue('');
            onPlaceSelected(emptyAddress, true);
            return;
        }
        // Check if it's a saved address (prefixed with 'saved-')
        if (selectedValue.startsWith('saved-')) {
            const savedAddressId = selectedValue.replace('saved-', '');
            const savedAddress = savedAddresses?.find((addr) => addr.id === savedAddressId);
            if (savedAddress) {
                const formattedAddress = formatServiceAddress(savedAddress.address, { partial, withCountry: showCountry });
                if (fieldName)
                    methods.setValue(fieldName, formattedAddress);
                if (showFullAddressInInput) {
                    setDisplayValue(formattedAddress);
                }
                else {
                    setDisplayValue(savedAddress.address.addressLine1);
                }
                onPlaceSelected(savedAddress);
                return;
            }
        }
        // Otherwise, it's a Google Places selection
        const suggestion = suggestions.find((prediction) => prediction.placePrediction?.placeId === selectedValue);
        if (!suggestion) {
            if (inputProps?.allowCustomValues) {
                setDisplayValue(selectedValue);
                onPlaceSelected({
                    ...emptyAddress,
                    name: selectedValue,
                    address: { ...emptyAddress.address, addressLine1: selectedValue },
                });
                return;
            }
            else {
                console.warn('The user selected a suggestion that does not exist.');
                setDisplayValue('');
                onPlaceSelected(emptyAddress, true);
                return;
            }
        }
        const originalInputAddress = suggestion?.placePrediction?.text;
        const foundPlace = (await suggestion.placePrediction.toPlace().fetchFields({
            fields: ['displayName', 'formattedAddress', 'addressComponents'],
        })).place;
        if (!foundPlace) {
            console.error('Prediction not found');
            return;
        }
        try {
            const { place } = await foundPlace.fetchFields({
                fields: ['displayName', 'formattedAddress', 'addressComponents', 'location'],
            });
            // ERROR CHECKS
            if (!place.addressComponents) {
                console.error('Address components were unable to be grabbed');
                if (onError)
                    onError({ type: 'NOT_FOUND', message: 'No address components found' });
                setDisplayValue('');
                onPlaceSelected(emptyAddress, true);
                return;
            }
            const cleanAddress = formatAsAddress(place.addressComponents);
            // Check it has a street number
            if (!partial && !place.addressComponents.find((component) => component.types.includes('street_number'))) {
                const streetNumberFromOriginalInput = originalInputAddress?.toString().split(' ')[0];
                if (!streetNumberFromOriginalInput) {
                    onError?.({
                        type: 'BAD_INPUT',
                        message: 'Please enter a valid address. It must include a street number.',
                        address: formatAsAddress(place.addressComponents),
                        coordinates: place.location?.toJSON(),
                    });
                    setDisplayValue('');
                    onPlaceSelected(emptyAddress, true);
                    return;
                }
                cleanAddress.addressLine1 = streetNumberFromOriginalInput + ' ' + cleanAddress.addressLine1;
            }
            // Check it has a valid lat/lng
            const lat = place.location?.lat();
            const lng = place.location?.lng();
            if (!lat || !lng) {
                console.error('Lat or Lng not found');
                if (onError)
                    onError({ type: 'NOT_SUPPORTED', message: 'This location is not supported. Please try another address.' });
                setDisplayValue('');
                onPlaceSelected(emptyAddress, true);
                return;
            }
            // Clean address data
            const county = place.addressComponents.find((component) => component.types.includes('administrative_area_level_2'));
            const cleanCounty = countyCleaner(cleanAddress, county);
            const formattedAddress = formatServiceAddress(cleanAddress, { partial, withCountry: showCountry });
            // Set appropriate variables
            const serviceLocation = {
                address: cleanAddress,
                coordinates: {
                    lat: lat,
                    lng: lng,
                },
                county: cleanCounty || '',
            };
            if (fieldName)
                methods.setValue(fieldName, formattedAddress);
            if (showFullAddressInInput) {
                setDisplayValue(formattedAddress);
            }
            else {
                setDisplayValue(cleanAddress.addressLine1);
            }
            if (includeTimezone) {
                const timezone = await fetchTimezoneForLatLng(lat, lng, apiKey);
                serviceLocation.timeZone = {
                    timeZoneId: timezone?.timeZoneId,
                    timeZoneName: timezone?.timeZoneName,
                    rawOffsetSeconds: timezone?.rawOffset,
                    dstOffsetSeconds: timezone?.dstOffset,
                };
            }
            onPlaceSelected(serviceLocation);
        }
        catch (error) {
            console.error('Error fetching place predictions:', error);
            if (onError)
                onError({ type: 'NOT_SUPPORTED', message: 'This location is not supported. Please try another address.' });
            setDisplayValue('');
            onPlaceSelected(emptyAddress, true);
        }
    };
    const formatAsAddress = (addressComponents) => {
        const address = addressComponents.reduce((acc, component) => {
            if (component.types.includes('street_number')) {
                acc.addressLine1 = component.longText || '';
            }
            if (component.types.includes('route')) {
                if (acc.addressLine1) {
                    acc.addressLine1 += ` ${component.longText}`;
                }
                else {
                    acc.addressLine1 = component.longText || '';
                }
            }
            if (component.types.includes('locality')) {
                acc.city = component.longText || '';
            }
            else if (component.types.includes('neighborhood') && !acc.city) {
                acc.city = component.longText || '';
            }
            else if (component.types.includes('sublocality_level_1') && !acc.city) {
                acc.city = component.longText || '';
            }
            else if (component.types.includes('administrative_area_level_3') && !acc.city) {
                acc.city = component.longText || '';
            }
            if (component.types.includes('administrative_area_level_1')) {
                acc.state = component.shortText || '';
            }
            if (component.types.includes('postal_code')) {
                acc.zip = component.longText || '';
            }
            if (component.types.includes('country')) {
                acc.country = component.shortText || '';
            }
            if (!acc.addressLine1)
                acc.addressLine1 = '';
            if (!acc.city)
                acc.city = '';
            if (!acc.zip)
                acc.zip = '';
            return acc;
        }, {});
        return address;
    };
    const loadPredictions = useCallback(async (rawInput) => {
        const sanitizedInput = rawInput.trim();
        setHasValue(sanitizedInput.length > 3);
        if (sanitizedInput.length === 0) {
            setSuggestions([]);
            return;
        }
        try {
            if (!isLoaded || !places.current) {
                places.current = await loader.importLibrary('places');
            }
            setLoadingResults(true);
            const { AutocompleteSessionToken, AutocompleteSuggestion } = places.current;
            // Add an initial request body.
            const request = {
                input: sanitizedInput,
                language: 'en-US',
                region: 'us',
                includedRegionCodes: ['US'],
                ...searchOptions,
            };
            // Create a session token.
            const token = new AutocompleteSessionToken();
            // Add the token to the request.
            request.sessionToken = token;
            // Fetch autocomplete suggestions.
            const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
            setSuggestions(suggestions);
        }
        catch (error) {
            console.error('Error fetching place predictions:', error);
        }
        finally {
            setLoadingResults(false);
        }
    }, []);
    const debouncedLoadPredictions = useCallback(debounce(loadPredictions, 200), [loadPredictions]);
    const handleInputChange = (event) => {
        setQuery(event);
        debouncedLoadPredictions(event);
    };
    useEffect(() => {
        if (inputProps?.queryValue === '') {
            debouncedLoadPredictions.cancel();
            setSuggestions([]);
            setDisplayValue('');
            return;
        }
        if (inputProps?.queryValue) {
            setDisplayValue(inputProps.queryValue);
            debouncedLoadPredictions(inputProps.queryValue);
        }
    }, [inputProps?.queryValue]);
    // Build children for AutoComplete
    const autoCompleteChildren = useMemo(() => {
        const children = [];
        // If savedAddresses mode is enabled
        if (savedAddresses) {
            // Saved Addresses Section Header
            children.push(_jsx(AutoCompleteOption, { value: "saved-header", disabled: true, className: "bg-gray-50 font-semibold text-gray-700", children: "Saved Addresses" }, "saved-header"));
            // Saved Addresses Options
            if (filteredSavedAddresses.length > 0) {
                filteredSavedAddresses.forEach((address) => {
                    const formattedAddress = formatServiceAddress(address.address);
                    children.push(_jsx(AutoCompleteOption, { value: `saved-${address.id}`, children: _jsxs("div", { className: "flex flex-row items-center justify-between", children: [_jsx("span", { children: formattedAddress }), _jsx("span", { className: "text-primary-on-surface-text", children: address.isActive ? `Active (${address.activeCount})` : null })] }) }, `saved-${address.id}`));
                });
            }
            else {
                children.push(_jsx(AutoCompleteOption, { value: "saved-none", disabled: true, className: "text-gray-400", children: "None" }, "saved-none"));
            }
            // Add Address Section Header
            children.push(_jsx(AutoCompleteOption, { value: "google-header", disabled: true, className: "bg-gray-50 font-semibold text-gray-700", children: "Add Address" }, "google-header"));
        }
        // Google Places Results
        if (suggestions.length === 0 && hasValue && query.length > 0) {
            children.push(_jsx(AutoCompleteOption, { value: "", disabled: true, children: "No results found" }, "noPredictions"));
        }
        else {
            suggestions
                .filter((pred) => !!pred.placePrediction?.placeId)
                .forEach((prediction) => {
                children.push(_jsx(AutoCompleteOption, { value: prediction.placePrediction.placeId, children: `${prediction.placePrediction?.mainText?.text} ${prediction.placePrediction?.secondaryText?.text}` }, prediction.placePrediction?.placeId));
            });
        }
        return children;
    }, [savedAddresses, filteredSavedAddresses, suggestions, hasValue, query]);
    // Extract children from inputProps to avoid conflicts
    const { children: _, ...restInputProps } = inputProps || {};
    return (_jsx(AutoComplete, { "data-testid": savedAddresses ? 'hybrid-address-input' : 'google-places-input', label: inputProps?.label ?? 'Address', required: required, onQueryChange: (value) => handleInputChange(value), onSelect: (value) => handleSelect(value), disabled: disabled || !isLoaded, renderLabel: displayValue, externalFilter: true, loading: loadingResults || !isLoaded, allowChangeWhenLoading: isLoaded, hideClearButton: true, ...restInputProps, children: autoCompleteChildren }));
}
//# sourceMappingURL=places-input.js.map